import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Brand, Disclaimer } from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";
import { startGoogleLogin } from "@/lib/api";

const GoogleIcon = () => (
  <svg viewBox="0 0 48 48" className="h-5 w-5" aria-hidden>
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
  </svg>
);

const Label = ({ full, short, compact }) =>
  compact ? (<><span className="hidden sm:inline">{full}</span><span className="sm:hidden">{short}</span></>) : full;

export const SignInButton = ({ testid, className = "", compact = false }) => {
  const { user } = useAuth();
  const cls = `bg-[#0078D4] hover:bg-[#005A9E] text-white rounded-full ${className}`;
  if (user)
    return (
      <Button asChild className={cls} data-testid={testid}>
        <Link to={user.onboarding_complete ? "/dashboard" : "/onboarding"}><Label full="Go to my dashboard" short="Dashboard" compact={compact} /> <ArrowRight className="h-4 w-4 ml-2" /></Link>
      </Button>
    );
  return (
    <Button onClick={startGoogleLogin} className={cls} data-testid={testid}>
      <span className="bg-white rounded-full p-1 mr-2"><GoogleIcon /></span> <Label full="Sign In" short="Sign In" compact={compact} />
    </Button>
  );
};

export const PublicShell = ({ children, bare = false }) => bare ? (
  <div className="bg-white overflow-x-clip">
    <main>{children}</main>
    <Disclaimer />
  </div>
) : (
  <div className="min-h-screen flex flex-col bg-white overflow-x-clip">
    <header className="sticky top-0 z-50 pt-[env(safe-area-inset-top)] backdrop-blur-md bg-white/85 border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
        <div className="min-w-0"><Brand /></div>
        <div className="flex shrink-0 items-center gap-2 sm:gap-5">
          <Link to="/how-it-works" className="hidden sm:inline max-lg:py-3 text-sm font-medium text-slate-600 hover:text-slate-900" data-testid="header-how-it-works-link">How it works</Link>
          <SignInButton testid="header-signin-button" className="h-10 px-4 text-sm" compact />
        </div>
      </div>
    </header>
    <main className="flex-1">{children}</main>
    <Disclaimer />
  </div>
);
